import React, { useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

// --- Component Imports ---

import SignIn from "./Login";
import Error500 from "./pages/Error505";
import Watermark from "./components/Watermark/Watermark";
import CreatorAccessButton from "./components/CreatorAccessButton/CreatorAccessButton";
import ErrorBoundary from "./ErrorBoundary";
import Sidebar from "./components/Sidebar/Sidebar";
// import MarketplaceSidebar from "./components/MarketplaceSidebar/MarketPlaceSidebar";
import Navbar from "./components/NavBar/Navbar";
import Dashboard from "./pages/Dashboard/Dashboard";
import Analytics from "./pages/Analtytics/Analytics";
import TestManagement from "./pages/TestManagement/TestManagement";
import FormBuilder from "./components/FormBuilder/FormBuilder";
import UserTestPage from "./pages/UserTest/UserTestPage";
import Profile from "./pages/Profile/Profile";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DiscoverMoreTests from "./pages/DiscoverMoreTest/DiscoverMoreTest";
import SelfDiscoveryGate from "./pages/SelfDiscovery/SelfDiscoveryGate";
import SelfDiscoveryContinue from "./pages/SelfDiscovery/SelfDiscoveryContinue";
import "./App.scss";

// ---- Test Management View Controller ----
// currentView/editingTestId now live in the URL (?view=create|edit&testId=...)
// rather than local state, so AppLayout can tell (from the URL alone) when
// FormBuilder is showing and unmount the sidebar/navbar/watermark for it —
// the same way it already does for the /test/:testId flow.
const TestManagementWithNavigation = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentView = searchParams.get("view") || "management";
  const editingTestId = searchParams.get("testId");

  const handleEditTest = (testId) => {
    setSearchParams(testId ? { view: "edit", testId } : { view: "edit" });
  };

  const handleCreateTest = () => {
    setSearchParams({ view: "create" });
  };

  const handleBackToManagement = () => {
    setSearchParams({});
  };

  if (currentView === "management") {
    return (
      <TestManagement
        onEditTest={handleEditTest}
        onCreateTest={handleCreateTest}
      />
    );
  } else {
    return (
      <div>
        <FormBuilder
          mode={currentView === "create" ? "create" : "edit"}
          testId={editingTestId}
          onSave={handleBackToManagement}
          onBack={handleBackToManagement}
        />
      </div>
    );
  }
};

// Matches "/test/:testId" (and any nested path under it) so the whole
// test-taking experience — intro, questions, results — goes full-screen.
const isTestTakingPath = (pathname) => /^\/test\/[^/]+/.test(pathname);

// Matches "?view=create" or "?view=edit&testId=..." on ANY route so
// FormBuilder goes full-screen — it's the same chrome-hiding need as the
// test-taking route, just detected via the query string instead of a path
// segment, since Create/Edit doesn't change the pathname. Both
// TestManagementWithNavigation (/testmanagement) and Admin use this same
// ?view= convention, so this intentionally isn't scoped to one pathname —
// any page that adopts the convention gets full-screen FormBuilder for free.
const isFormBuilderFullScreen = (search) => {
  const view = new URLSearchParams(search).get("view");
  return view === "create" || view === "edit";
};

// ---- App Layout for Protected Routes ----
const AppLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const fullScreen =
    isTestTakingPath(location.pathname) ||
    isFormBuilderFullScreen(location.search);

  return (
    <div
      className={`app ${isSidebarOpen ? "mobile-nav-open" : ""} ${fullScreen ? "app--fullscreen" : ""}`}
    >
      {!fullScreen && <Watermark text="HRLabs" />}
      {!fullScreen && <CreatorAccessButton />}

      {!fullScreen && (
        <Sidebar
          isOpen={isSidebarOpen}
          onNavigate={() => setIsSidebarOpen(false)}
        />
      )}

      {!fullScreen && (
        <button
          type="button"
          className="mobile-nav-backdrop"
          aria-label="Close navigation menu"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* <MarketplaceSidebar /> */}

      {!fullScreen && (
        <Navbar onMenuToggle={() => setIsSidebarOpen((isOpen) => !isOpen)} />
      )}

      <main
        className={`main-content ${fullScreen ? "main-content--full" : ""}`}
      >
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route
            path="/testmanagement"
            element={<TestManagementWithNavigation />}
          />
          <Route path="/test/:testId" element={<UserTestPage />} />
          <Route path="/profile" element={<Profile />} />
          <Route
            path="/user-history"
            element={<Navigate to="/discovermoretests" replace />}
          />
          <Route path="/discovermoretests" element={<DiscoverMoreTests />} />
          <Route path="/results" element={<Dashboard />} />
          <Route path="/testpage" element={<Dashboard />} />
          <Route path="/error-500" element={<Error500 />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
};

// ---- Main App ----
function App() {
  return (
    <>
      <Router>
        <AuthProvider>
          <ErrorBoundary>
            <Routes>
              <Route path="/" element={<SignIn />} />

              {/* Self-discovery test links. Both routes render the SAME
                  login page (SignIn) — SelfDiscoveryGate just stashes the
                  decoded token first so Login.jsx knows to redirect to
                  /self-discovery/continue instead of /dashboard once the
                  person authenticates. Deliberately outside
                  ProtectedRoute/AppLayout so there's no sidebar chrome. */}
              <Route
                path="/self-discovery/test"
                element={<SelfDiscoveryGate />}
              />
              <Route
                path="/self-discovery/public/test"
                element={<SelfDiscoveryGate />}
              />
              <Route
                path="/self-discovery/continue"
                element={<SelfDiscoveryContinue />}
              />

              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </ErrorBoundary>{" "}
        </AuthProvider>
      </Router>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
}

export default App;
